import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { format } from 'date-fns';
import BACKEND_URL from '../config/api';

const BlogEditorPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [categoryInput, setCategoryInput] = useState('');
  const [tagInput, setTagInput] = useState('');
  
  // AI Assistant State
  const [showAI, setShowAI] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiTone, setAiTone] = useState('professional');
  const [aiLength, setAiLength] = useState('medium');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiVersions, setAiVersions] = useState([]);
  const [showVersionSelector, setShowVersionSelector] = useState(false);
  
  // Scheduling State
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [publishOption, setPublishOption] = useState('now'); // 'now' or 'schedule'
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  
  // References State
  const [references, setReferences] = useState([
    { title: '', url: '' },
    { title: '', url: '' },
    { title: '', url: '' }
  ]);
  
  // File Upload State
  const [coverImageFile, setcoverImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Rich Text Editor Configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image', 'video'],
      ['blockquote', 'code-block'],
      ['clean']
    ]
  };

  useEffect(() => {
    // Wait for auth to load before checking user
    if (authLoading) {
      return;
    }
    
    if (!user) {
      // Save current path to redirect back after login
      sessionStorage.setItem('redirectAfterLogin', '/blog/editor');
      navigate('/login');
      return;
    }
    
    if (slug) {
      loadPost();
    }
  }, [user, slug, authLoading]);

  const loadPost = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_URL}/api/blog/posts/${slug}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success && data.post) {
        const post = data.post;
        setTitle(post.title);
        setContent(post.content?.text || '');
        setExcerpt(post.excerpt);
        setCoverImage(post.cover_image_url || '');
        setCategories(post.categories || []);
        setTags(post.tags || []);
        setReferences(post.references || [{ title: '', url: '' }, { title: '', url: '' }, { title: '', url: '' }]);
      }
    } catch (error) {
      console.error('Error loading post:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${BACKEND_URL}/api/blog/media/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        setCoverImage(data.file_url);
        alert('Image uploaded successfully!');
      } else {
        alert('Failed to upload image: ' + data.message);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }
      setcoverImageFile(file);
      handleImageUpload(file);
    }
  };

  const handleReferenceChange = (index, field, value) => {
    const newReferences = [...references];
    newReferences[index][field] = value;
    setReferences(newReferences);
  };

  const getValidReferences = () => {
    return references.filter(ref => ref.title.trim() && ref.url.trim());
  };

  const handleSaveDraft = async () => {
    if (!title.trim()) {
      alert('Please enter a title');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const postData = {
        title,
        content: { text: content, blocks: [] },
        excerpt: excerpt || title.substring(0, 150),
        cover_image_url: coverImage,
        categories,
        tags,
        references: getValidReferences(),
        meta_title: title,
        meta_description: excerpt || title.substring(0, 150)
      };

      const response = await fetch(`${BACKEND_URL}/api/blog/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(postData)
      });

      const data = await response.json();
      if (data.success) {
        alert('Draft saved successfully!');
        navigate('/profile');
      } else {
        alert('Failed to save draft: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (scheduled = null) => {
    if (!title.trim() || !content.trim()) {
      alert('Please enter title and content');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      
      // First save as draft
      const postData = {
        title,
        content: { text: content, blocks: [] },
        excerpt: excerpt || title.substring(0, 150),
        cover_image_url: coverImage,
        categories,
        tags,
        references: getValidReferences(),
        meta_title: title,
        meta_description: excerpt || title.substring(0, 150)
      };

      // If scheduled, add scheduled_at field
      if (scheduled) {
        postData.scheduled_at = scheduled;
      }

      const createResponse = await fetch(`${BACKEND_URL}/api/blog/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(postData)
      });

      const createData = await createResponse.json();
      if (createData.success && createData.post) {
        // Then publish it
        const publishResponse = await fetch(
          `${BACKEND_URL}/api/blog/posts/${createData.post.post_id}/publish`,
          {
            method: 'POST',
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        const publishData = await publishResponse.json();
        if (publishData.success) {
          if (scheduled) {
            alert('Post scheduled successfully!');
            navigate('/profile');
          } else {
            alert('Post published successfully!');
            navigate(`/blog/${publishData.post.slug}`);
          }
        } else {
          alert('Failed to publish: ' + publishData.message);
        }
      } else {
        alert('Failed to create post: ' + (createData.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error publishing:', error);
      alert('Failed to publish post: ' + error.message);
    } finally {
      setSaving(false);
      setShowScheduleModal(false);
    }
  };

  const handlePublishClick = () => {
    setShowScheduleModal(true);
  };

  const handleScheduleSubmit = () => {
    if (publishOption === 'schedule') {
      if (!scheduledDate || !scheduledTime) {
        alert('Please select date and time for scheduling');
        return;
      }
      const scheduledDateTime = `${scheduledDate}T${scheduledTime}:00`;
      handlePublish(scheduledDateTime);
    } else {
      handlePublish(null);
    }
  };

  const handleAIGenerate = async () => {
    if (!aiTopic.trim()) {
      alert('Please enter a topic');
      return;
    }

    setAiLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_URL}/api/blog/ai/generate-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          topic: aiTopic,
          tone: aiTone,
          length: aiLength
        })
      });

      const data = await response.json();
      if (data.success && data.versions) {
        setAiVersions(data.versions);
        setShowVersionSelector(true);
        setShowAI(false);
      } else {
        alert('AI generation failed: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('AI generation error:', error);
      alert('Failed to generate content: ' + error.message);
    } finally {
      setAiLoading(false);
    }
  };

  const handleSelectVersion = (version) => {
    setContent(version.content);
    setTitle(aiTopic);
    setShowVersionSelector(false);
    alert(`Version ${version.version} selected! You can now edit and improve it.`);
  };

  const addCategory = () => {
    if (categoryInput.trim() && !categories.includes(categoryInput.trim())) {
      setCategories([...categories, categoryInput.trim().toLowerCase().replace(/ /g, '-')]);
      setCategoryInput('');
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  // Show loading state while auth is being checked
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
          <p className="text-gray-600">Loading editor...</p>
        </div>
      </div>
    );
  }

  // Show loading state if redirecting to login
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
          <p className="text-gray-600">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
              RecruiterIQ
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/blog" className="text-gray-600 hover:text-purple-600 transition">← Back to Blog</Link>
              <Link to="/profile" className="text-gray-600 hover:text-purple-600 transition">Profile</Link>
            </div>
          </div>
        </div>
      </nav>
      
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-gray-800">
              {slug ? 'Edit Post' : 'Create New Post'}
            </h1>
            <div className="flex gap-3">
              <button
                onClick={() => setShowAI(!showAI)}
                className="px-6 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-lg hover:from-purple-600 hover:to-indigo-600 transition flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                AI Assistant
              </button>
              <button
                onClick={handleSaveDraft}
                disabled={saving}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Draft'}
              </button>
              <button
                onClick={handlePublishClick}
                disabled={saving}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
              >
                {saving ? 'Publishing...' : 'Publish'}
              </button>
            </div>
          </div>
        </div>

        {/* AI Assistant Panel */}
        {showAI && (
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">AI Content Generator</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Topic</label>
                <input
                  type="text"
                  value={aiTopic}
                  onChange={(e) => setAiTopic(e.target.value)}
                  placeholder="E.g., Future of AI in Recruiting"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tone</label>
                <select
                  value={aiTone}
                  onChange={(e) => setAiTone(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="professional">Professional</option>
                  <option value="casual">Casual</option>
                  <option value="technical">Technical</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Length</label>
                <select
                  value={aiLength}
                  onChange={(e) => setAiLength(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="short">Short (500-800 words)</option>
                  <option value="medium">Medium (1000-1500 words)</option>
                  <option value="long">Long (2000-3000 words)</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleAIGenerate}
                  disabled={aiLoading}
                  className="w-full px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50"
                >
                  {aiLoading ? 'Generating...' : 'Generate Content'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Editor */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          {/* Title */}
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Post Title..."
            className="w-full text-4xl font-bold border-none focus:outline-none mb-6 placeholder-gray-300"
          />

          {/* Cover Image */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Cover Image</label>
            
            {/* Image Preview */}
            {coverImage && (
              <div className="mb-3">
                <img src={coverImage} alt="Cover preview" className="w-full h-48 object-cover rounded-lg" />
              </div>
            )}
            
            {/* Upload Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* File Upload */}
              <div>
                <label className="block text-xs text-gray-600 mb-1">Upload Image File</label>
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageFileChange}
                    disabled={uploadingImage}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                  />
                  {uploadingImage && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg">
                      <div className="text-purple-600">Uploading...</div>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">Max 5MB • JPG, PNG, WebP</p>
              </div>
              
              {/* URL Input */}
              <div>
                <label className="block text-xs text-gray-600 mb-1">Or Enter Image URL</label>
                <input
                  type="text"
                  value={coverImage}
                  onChange={(e) => setCoverImage(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
                <p className="text-xs text-gray-500 mt-1">External image URL</p>
              </div>
            </div>
          </div>

          {/* Excerpt */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Excerpt (Optional)</label>
            <textarea
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              placeholder="Brief description..."
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Content - Rich Text Editor */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
            <ReactQuill
              theme="snow"
              value={content}
              onChange={setContent}
              modules={quillModules}
              placeholder="Write your story..."
              className="bg-white"
              style={{ height: '400px', marginBottom: '60px' }}
            />
          </div>

          {/* Categories & Tags */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Categories</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={categoryInput}
                  onChange={(e) => setCategoryInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCategory())}
                  placeholder="Add category..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
                <button
                  onClick={addCategory}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {categories.map((cat, i) => (
                  <span key={i} className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                    {cat}
                    <button onClick={() => setCategories(categories.filter((_, idx) => idx !== i))}>×</button>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  placeholder="Add tag..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
                <button
                  onClick={addTag}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag, i) => (
                  <span key={i} className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                    {tag}
                    <button onClick={() => setTags(tags.filter((_, idx) => idx !== i))}>×</button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* References Section */}
          <div className="mt-6 p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border-2 border-blue-200">
            <div className="flex items-center gap-2 mb-4">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-800">Reference Links & Attachments</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">Add up to 3 reference links or resources mentioned in your article</p>
            
            {references.map((ref, index) => (
              <div key={index} className="mb-4 p-4 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
                    {index + 1}
                  </span>
                  <span className="text-sm font-medium text-gray-700">Reference {index + 1} (Optional)</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={ref.title}
                    onChange={(e) => handleReferenceChange(index, 'title', e.target.value)}
                    placeholder="Title (e.g., Research Paper, Article, Tool)"
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                  <input
                    type="url"
                    value={ref.url}
                    onChange={(e) => handleReferenceChange(index, 'url', e.target.value)}
                    placeholder="URL (e.g., https://example.com)"
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
              </div>
            ))}
            
            <div className="mt-4 p-3 bg-blue-100 rounded-lg">
              <p className="text-xs text-blue-800 flex items-start gap-2">
                <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <span>References will appear at the bottom of your article with clickable links for readers to explore further.</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* AI Version Selector Modal */}
      {showVersionSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full p-8 my-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl font-bold text-gray-800">Choose Your Favorite Version</h2>
              <button onClick={() => setShowVersionSelector(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <p className="text-gray-600 mb-6">AI generated 2 versions with different approaches. Select one to use as your starting point, then edit and improve it!</p>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {aiVersions.map((version) => (
                <div key={version.version} className="border-2 border-gray-200 rounded-xl p-6 hover:border-purple-500 transition">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-purple-600">{version.title}</h3>
                      <p className="text-sm text-gray-500">{version.approach} • {version.word_count} words</p>
                    </div>
                    <span className="text-4xl font-bold text-gray-200">{version.version}</span>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4 mb-4 max-h-96 overflow-y-auto">
                    <div 
                      className="prose prose-sm max-w-none text-gray-700"
                      dangerouslySetInnerHTML={{ __html: version.content.substring(0, 1000) + '...' }}
                    />
                  </div>
                  
                  <button
                    onClick={() => handleSelectVersion(version)}
                    className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition font-semibold"
                  >
                    Choose Version {version.version}
                  </button>
                </div>
              ))}
            </div>
            
            <div className="mt-6 text-center">
              <button
                onClick={() => setShowVersionSelector(false)}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 transition"
              >
                Cancel & Try Again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Publish Options</h2>
            
            {/* Publish Now Option */}
            <div className="mb-4">
              <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-purple-50 transition">
                <input
                  type="radio"
                  name="publishOption"
                  value="now"
                  checked={publishOption === 'now'}
                  onChange={(e) => setPublishOption(e.target.value)}
                  className="w-5 h-5 text-purple-600"
                />
                <div className="ml-4">
                  <div className="font-semibold text-gray-800">Publish Now</div>
                  <div className="text-sm text-gray-600">Post will be live immediately</div>
                </div>
              </label>
            </div>

            {/* Schedule Later Option */}
            <div className="mb-6">
              <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-purple-50 transition">
                <input
                  type="radio"
                  name="publishOption"
                  value="schedule"
                  checked={publishOption === 'schedule'}
                  onChange={(e) => setPublishOption(e.target.value)}
                  className="w-5 h-5 text-purple-600"
                />
                <div className="ml-4">
                  <div className="font-semibold text-gray-800">Schedule for Later</div>
                  <div className="text-sm text-gray-600">Choose date and time to publish</div>
                </div>
              </label>

              {/* Date/Time Picker */}
              {publishOption === 'schedule' && (
                <div className="mt-4 ml-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <input
                      type="date"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      min={format(new Date(), 'yyyy-MM-dd')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                    <input
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowScheduleModal(false)}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleScheduleSubmit}
                disabled={saving}
                className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
              >
                {saving ? 'Publishing...' : publishOption === 'schedule' ? 'Schedule' : 'Publish Now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogEditorPage;
